import { FetchArgs, FetchBaseQueryMeta } from "@reduxjs/toolkit/query";
import { Mutex } from "async-mutex";
import { RefreshTokenResponse } from "../../../../models/authentication/refreshTokenResponse";
import { TBaseQuery } from "../../../../models/baseQueries/baseQuery";
import envSettings from "../../../../services/system/envSettings";
import { refreshTokenExtension } from "../refreshTokenApiExt";

export const baseQueryWithRefreshToken: (baseQuery: TBaseQuery, mutex: Mutex) => TBaseQuery =
  (baseQuery: TBaseQuery, mutex: Mutex) => async (args, api, extraOptions) => {
    // wait until the mutex is available without locking it
    await mutex.waitForUnlock();

    let result = await baseQuery(args, api, extraOptions);

    if (result.error?.status === 401) {
      // it omits the refresh token for the "/utils/initialize-token" endpoint.
      // if it returns 401, the user has no access to the system and needs to log-in.
      const queryArgs = args as FetchArgs;
      const initializeTokenUrl = `${envSettings.UTILS_API_URL}/${envSettings.INITIALIZE_TOKEN_PREF}`;
      if (queryArgs.url === initializeTokenUrl) {
        return result;
      }

      if (!mutex.isLocked()) {
        const release = await mutex.acquire();
        try {
          const refreshTokenResult = await refreshTokenExtension(baseQuery, api, extraOptions);
          const refreshTokenData = (
            refreshTokenResult as {
              data: RefreshTokenResponse;
              meta: FetchBaseQueryMeta | undefined;
            }
          )?.data;
          if (refreshTokenData) {
            // store the new token
            api.dispatch({
              type: "authSlice/setRefreshToken",
              payload: refreshTokenData,
            });
            // retry the initial query
            result = await baseQuery(args, api, extraOptions);
          } else {
            api.dispatch({
              type: "authSlice/logout",
              payload: { redirectUrl: envSettings.LOG_OFF_EXT },
            });
          }
        } finally {
          // release must be called once the mutex should be released again.
          release();
        }
      } else {
        // wait until the mutex is available without locking it
        await mutex.waitForUnlock();
        result = await baseQuery(args, api, extraOptions);
      }
    }
    return result;
  };
